import { Component, ElementRef, inject, input, OnInit, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { NgxMaskPipe } from 'ngx-mask';
import { PHONE_NUMBER_MASK } from '../../constants';
import { CustomerContactInfoDto } from '../../dtos/customer-contact-info.dto';
import { CustomerService } from '../../services/customer.service';
import { ScrollToService } from '../../services/scroll-to/scroll-to.service';
import { DeviceService } from '../../services/device-detector/device.service';
import { IconComponent } from '../icon/icon.component';
import { UpdateEmailModalComponent } from './components/update-email-modal/update-email-modal.component';
import { UpdatePhoneModalComponent } from './components/update-phone-modal/update-phone-modal.component';
import { ConfirmPhoneModalComponent } from '../confirm-phone-modal/confirm-phone-modal.component';
import { ConfirmEmailModalComponent } from '../confirm-email-modal/confirm-email-modal.component';
import { UpdateFullNameModalComponent } from './components/update-full-name-modal/update-full-name-modal.component';
import { AnalyticsService } from '../../services/analytics.service';

@Component({
  selector: 'customer-info',
  templateUrl: './customer-info.component.html',
  styleUrls: ['./customer-info.component.scss'],
  imports: [ReactiveFormsModule, TranslatePipe, NgxMaskPipe, IconComponent, MatTooltipModule],
})
export class CustomerInfoComponent implements OnInit {
  customerService = inject(CustomerService);
  private scrollToService = inject(ScrollToService);
  private elementRef = inject(ElementRef);
  private dialog = inject(MatDialog);
  private deviceService = inject(DeviceService);
  private analyticsService = inject(AnalyticsService);

  source = input<string>('');

  isErrorsVisible = signal<boolean>(false);

  ngOnInit(): void {
  }

  checkValidity(): boolean {
    this.resetErrors();
    const isValid: boolean = this.isValid();

    if (!isValid) {
      this.isErrorsVisible.set(true);
      this.scrollToService.scrollTo({ target: this.elementRef, offset: -50 });
    }

    return isValid;
  }

  isValid(): boolean {
    let hasContactInfo = !!this.customerService.customer2()?.contactInfo.phoneNumber
      && !!this.customerService.customer2()?.contactInfo.lastName
      && !!this.customerService.customer2()?.contactInfo.firstName;
    if (this.customerService.customer2().contactInfo.isPhoneNumberNotSupported) {
      hasContactInfo = hasContactInfo && !!this.customerService.customer2().contactInfo.email;
    }

    return this.customerService.isLoggedIn && hasContactInfo;
  }

  protected isControlInvalid(key: 'phoneNumber' | 'email' | 'name'): boolean {
    let isValid: boolean = false;
    switch (key) {
      case 'phoneNumber':
        isValid = !!this.customerService.customer2().contactInfo.phoneNumber;
        break;
      case 'email':
        isValid = this.customerService.customer2().contactInfo.isPhoneNumberNotSupported
          ? !!this.customerService.customer2().contactInfo.email
          : true;
        break;
      case 'name':
        isValid = !!this.customerService.customer2().contactInfo.lastName
          && !!this.customerService.customer2().contactInfo.firstName;
        break;
    }

    return this.isErrorsVisible() && !isValid;
  }

  private resetErrors(): void {
    this.isErrorsVisible.set(false);
  }

  onConfirmPhoneNumber(): void {
    this.dialog.open(ConfirmPhoneModalComponent);
    this.analyticsService.addNewRecord({
      event: 'Open confirm phone number modal',
      source: this.source(),
      customerId: this.customerService.customer2().id,
      phoneNumber: this.customerService.customer2().contactInfo.phoneNumber,
    });
  }

  onPhoneNumberInputClick(): void {
    if (this.deviceService.isDesktop() && this.customerService.customer2().contactInfo.phoneNumber) {
      return;
    }
    this.onChangePhoneNumber();
  }

  onChangePhoneNumber(source?: string): void {
    this.dialog.open(UpdatePhoneModalComponent);
    this.analyticsService.addNewRecord({
      event: 'Open update phone number modal',
      source: `${this.source()} - ${source}`,
      customerId: this.customerService.customer2().id,
      phoneNumber: this.customerService.customer2().contactInfo.phoneNumber,
    });
  }

  onConfirmEmail(): void {
    this.dialog.open(ConfirmEmailModalComponent);
    this.analyticsService.addNewRecord({
      event: 'Open confirm email modal',
      source: this.source(),
      customerId: this.customerService.customer2().id,
      email: this.customerService.customer2().contactInfo.email,
    });
  }

  onEmailInputClick(): void {
    if (this.deviceService.isDesktop() && this.customerService.customer2().contactInfo.email) {
      return;
    }
    this.onChangeEmail();
  }

  onChangeEmail(source?: string): void {
    this.dialog.open(UpdateEmailModalComponent);
    this.analyticsService.addNewRecord({
      event: 'Open update email modal',
      source: `${this.source()} - ${source}`,
      customerId: this.customerService.customer2().id,
      email: this.customerService.customer2().contactInfo.email,
    });
  }

  onNameInputClick(): void {
    if (this.deviceService.isDesktop() && this.customerService.customer2().contactInfo.lastName && this.customerService.customer2().contactInfo.firstName) {
      return;
    }
    this.onChangeName();
  }

  onChangeName(): void {
    this.dialog.open(UpdateFullNameModalComponent);
    this.analyticsService.addNewRecord({
      event: 'Open update full name modal',
      source: this.source(),
      customerId: this.customerService.customer2().id,
      fullName: `${this.customerService.customer2().contactInfo.lastName} ${this.customerService.customer2().contactInfo.firstName} ${this.customerService.customer2().contactInfo.middleName}`,
    });
  }

  getValue(): CustomerContactInfoDto {
    return this.customerService.customer2()?.contactInfo;
  }

  protected readonly PHONE_NUMBER_MASK = PHONE_NUMBER_MASK;
}
